/** TanStack Query hooks. staleTime is matched to the backend's cache TTL. */
import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import { useFilterStore } from "@/store/filters";
import type {
  AnalyticsEnvelope, ExecutiveSummary, Insight, JourneyEvent, QualityIssue,
} from "@/types/api";

export function useExecutiveDashboard() {
  const params = useFilterStore((s) => s.queryParams());
  return useQuery({
    queryKey: ["executive", params],
    queryFn: async () => {
      const { data } = await api.get<AnalyticsEnvelope<ExecutiveSummary>>(
        "/dashboard/executive", { params },
      );
      return data;
    },
    staleTime: 60_000,
  });
}

export function useFunnel() {
  const params = useFilterStore((s) => s.queryParams());
  return useQuery({
    queryKey: ["funnel", params],
    queryFn: async () => {
      const { data } = await api.get<AnalyticsEnvelope>("/analytics/funnel", { params });
      return data;
    },
    staleTime: 300_000,
  });
}

export function useVpRanking() {
  const params = useFilterStore((s) => s.queryParams());
  return useQuery({
    queryKey: ["vp-ranking", params],
    queryFn: async () => {
      const { data } = await api.get<{ breakdown: Array<Record<string, unknown>> }>(
        "/analytics/vps/ranking", { params },
      );
      return data.breakdown;
    },
    staleTime: 300_000,
  });
}

export function useClientProfile(clientId: string | undefined) {
  return useQuery({
    enabled: Boolean(clientId),
    queryKey: ["client", clientId],
    queryFn: async () => {
      const { data } = await api.get<AnalyticsEnvelope>(`/clients/${clientId}/profile`);
      return data;
    },
  });
}

export function useEmployeeProfile(personKey: string | undefined) {
  const params = useFilterStore((s) => s.queryParams());
  return useQuery({
    enabled: Boolean(personKey),
    queryKey: ["employee", personKey, params],
    queryFn: async () => {
      const { data } = await api.get<AnalyticsEnvelope>(
        `/employees/${personKey}/profile`, { params },
      );
      return data;
    },
  });
}

export function useTimeline(
  kind: "client" | "lead" | "employee",
  id: string | undefined,
  /** True ignores the period filter and returns the whole relationship. */
  allTime = false,
) {
  // The filter bar's period is sent through, so a period the user set actually
  // changes the journey. `allTime` is the deliberate override.
  const params = useFilterStore((s) => s.queryParams());
  return useQuery({
    enabled: Boolean(id),
    queryKey: ["timeline", kind, id, allTime ? "all" : params],
    queryFn: async () => {
      const { data } = await api.get<{
        events: JourneyEvent[];
        metadata: { count: number; inferred_count: number };
      }>(`/timeline/${kind}/${id}`, {
        params: allTime ? { all_time: "true" } : params,
      });
      return data;
    },
    staleTime: 120_000,
  });
}

export function useInsights(kind?: "opportunity" | "risk") {
  return useQuery({
    queryKey: ["insights", kind],
    queryFn: async () => {
      const { data } = await api.get<{ items: Insight[]; metadata: { count: number } }>(
        "/insights", { params: kind ? { kind } : {} },
      );
      return data;
    },
    staleTime: 300_000,
  });
}

export function useQualityScorecard() {
  return useQuery({
    queryKey: ["quality"],
    queryFn: async () => {
      const { data } = await api.get<{ items: QualityIssue[] }>("/quality/scorecard");
      return data.items;
    },
    staleTime: 600_000,
  });
}

export function useFilterOptions(dimension: string, parent?: string | null) {
  return useQuery({
    queryKey: ["filters", dimension, parent],
    queryFn: async () => {
      const { data } = await api.get<{
        options: Array<{ value: string; label: string; group?: string; needs_review?: boolean }>;
      }>(`/filters/${dimension}`, { params: parent ? { parent } : {} });
      return data.options;
    },
    staleTime: 900_000,
  });
}
